import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isNativePlatform } from '../lib/capacitor';
import { API_URL } from '../lib/apiUrl';
import { Geolocation } from '@capacitor/geolocation';
import './LiveTracking.css';

const UPDATE_INTERVAL = 15000; // 15 seconds

export default function LiveTracking({ shortCode, shareUrl, onClose }) {
  const { getAccessToken } = useAuth();
  const toast = useToast();
  const [isTracking, setIsTracking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastPositionRef = useRef(null);

  const sendLocationUpdate = useCallback(async (position) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_URL}/api/shared-trips/${shortCode}/location`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          tracking_active: true
        })
      });

      if (response.ok) {
        setLastUpdate(new Date());
        setError(null);
      }
    } catch (err) {
      console.error('Location update error:', err);
      setError('Failed to send location update');
    }
  }, [shortCode, getAccessToken]);

  const startTracking = useCallback(() => {
    setIsTracking(true);
    setError(null);

    const watchOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000
    };

    const onPosition = (position) => {
      lastPositionRef.current = position;
      // Send immediately on first position
      if (!lastUpdate) {
        sendLocationUpdate(position);
      }
    };

    const onError = (err) => {
      console.error('Geolocation error:', err);
      setError('Could not get your location. Please enable location services.');
    };

    if (isNativePlatform()) {
      // Request permissions first on native (required on iOS)
      Geolocation.requestPermissions().then(() => {
      Geolocation.watchPosition(watchOptions, (position, err) => {
        if (err) { onError(err); return; }
        if (position) onPosition(position);
      }).then(id => { watchIdRef.current = id; });
      }).catch(onError);
    } else {
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by your browser');
        return;
      }
      watchIdRef.current = navigator.geolocation.watchPosition(onPosition, onError, watchOptions);
    }

    // Send updates at regular intervals
    intervalRef.current = setInterval(() => {
      if (lastPositionRef.current) {
        sendLocationUpdate(lastPositionRef.current);
      }
    }, UPDATE_INTERVAL);

    toast.success('Live tracking started!');
  }, [lastUpdate, sendLocationUpdate, toast]);

  const stopTracking = useCallback(async () => {
    // Clear watch and interval
    if (watchIdRef.current !== null) {
      if (isNativePlatform()) {
        await Geolocation.clearWatch({ id: watchIdRef.current });
      } else {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Notify backend that tracking stopped
    try {
      const token = await getAccessToken();
      await fetch(`${API_URL}/api/shared-trips/${shortCode}/stop-tracking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      // Silent fail - tracking cleanup is best-effort
    }

    setIsTracking(false);
    toast.info('Live tracking stopped');
  }, [shortCode, getAccessToken, toast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        if (isNativePlatform()) {
          Geolocation.clearWatch({ id: watchIdRef.current });
        } else if (navigator.geolocation) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Could not copy link');
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="live-tracking">
      <div className="tracking-header">
        <h3>Live Location Sharing</h3>
        <button className="tracking-close" onClick={onClose} aria-label="Close live tracking">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="tracking-link">
        <input type="text" value={shareUrl} readOnly />
        <button onClick={handleCopyLink}>Copy</button>
      </div>

      <p className="tracking-info">
        Share this link with someone so they can see your live location on a map.
      </p>

      {error && (
        <div className="tracking-error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
        </div>
      )}

      {isTracking ? (
        <div className="tracking-active">
          <div className="tracking-pulse"></div>
          <div className="tracking-status">
            <span className="status-text">Sharing your location</span>
            {lastUpdate && (
              <span className="last-update">Last update: {formatTime(lastUpdate)}</span>
            )}
          </div>
          <button className="stop-tracking-btn" onClick={stopTracking}>
            Stop Sharing
          </button>
        </div>
      ) : (
        <button className="start-tracking-btn" onClick={startTracking}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          Start Live Tracking
        </button>
      )}

      <p className="tracking-note">
        Your location updates every 15 seconds while tracking is active.
        Battery usage may increase.
      </p>
    </div>
  );
}
